import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, Download, Mail, CheckCircle, Percent, X } from 'lucide-react';
import api from '../api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const fmtCurrency = (v) => `$${Number(v || 0).toFixed(2)}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '';

export default function AccountStatementDetail() {
  const { customerName } = useParams();
  const navigate = useNavigate();
  const decoded = decodeURIComponent(customerName);

  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [serviceRate, setServiceRate] = useState(1.5);
  const [showEmail, setShowEmail] = useState(false);
  const [emailAddr, setEmailAddr] = useState('');
  const [emailMsg, setEmailMsg] = useState('');
  const [emailing, setEmailing] = useState(false);

  const loadStatement = async (rate) => {
    setLoading(true);
    try {
      const data = await api.request(`/api/statements/customer/${encodeURIComponent(decoded)}?service_charge_rate=${rate || serviceRate}`);
      setStatement(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadStatement(); }, []); // eslint-disable-line

  const markPaid = async (orderId) => {
    if (!window.confirm('Mark this invoice as paid?')) return;
    try {
      await api.request(`/api/statements/mark-paid/${orderId}`, { method: 'PUT' });
      loadStatement();
    } catch (err) { alert('Failed: ' + err.message); }
  };

  const markAllPaid = async () => {
    const pending = (statement?.invoices || []).filter(i => i.payment_status === 'pending').length;
    if (!window.confirm(`Mark all ${pending} pending invoices as paid?`)) return;
    try {
      await api.request(`/api/statements/mark-all-paid/${encodeURIComponent(decoded)}`, { method: 'PUT' });
      loadStatement();
    } catch (err) { alert('Failed: ' + err.message); }
  };

  const downloadPdf = () => {
    const token = localStorage.getItem('access_token');
    fetch(`${API_URL}/api/statements/customer/${encodeURIComponent(decoded)}/pdf?service_charge_rate=${serviceRate}`,
      { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Statement-${decoded.replace(/\s+/g, '_')}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(err => alert('Download failed: ' + err.message));
  };

  const sendEmail = async () => {
    if (!emailAddr) { alert('Enter an email address'); return; }
    setEmailing(true);
    try {
      await api.request(`/api/statements/customer/${encodeURIComponent(decoded)}/email?service_charge_rate=${serviceRate}`,
        { method: 'POST', body: JSON.stringify({ email: emailAddr, message: emailMsg }) });
      alert('Statement emailed successfully!');
      setShowEmail(false);
    } catch (err) { alert('Failed: ' + err.message); }
    finally { setEmailing(false); }
  };

  const updateRate = (val) => {
    const r = parseFloat(val) || 0;
    setServiceRate(r);
    loadStatement(r);
  };

  if (loading) return <div className="p-8 text-center">Loading statement...</div>;
  if (!statement) return <div className="p-8 text-center text-red-500">Statement not found</div>;

  const pendingInvoices = statement.invoices.filter(i => i.payment_status !== 'completed');
  const paidInvoices = statement.invoices.filter(i => i.payment_status === 'completed');

  return (
    <div data-testid="statement-detail-page">
      {/* Controls bar - hidden when printing */}
      <div className="print:hidden bg-card border-b px-6 py-3 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={() => navigate(-1)} className="p-2 rounded hover:bg-muted" data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <span className="text-sm text-muted-foreground flex-1">Statement — {decoded}</span>
        <div className="flex items-center gap-1.5 mr-2">
          <Percent className="h-3.5 w-3.5 text-muted-foreground" />
          <input type="number" step="0.1" min="0" max="10" value={serviceRate}
            onChange={e => updateRate(e.target.value)}
            className="w-14 px-2 py-1 border border-input rounded text-xs text-center" data-testid="rate-input" />
          <span className="text-xs text-muted-foreground">%/mo</span>
        </div>
        {pendingInvoices.length > 0 && (
          <button onClick={markAllPaid}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
            data-testid="mark-all-paid-btn">
            <CheckCircle className="h-3.5 w-3.5" /> Mark All Paid
          </button>
        )}
        <button onClick={downloadPdf}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-md hover:bg-muted" data-testid="download-pdf-btn">
          <Download className="h-3.5 w-3.5" /> PDF
        </button>
        <button onClick={() => setShowEmail(!showEmail)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-md hover:bg-muted" data-testid="email-btn">
          <Mail className="h-3.5 w-3.5" /> Email
        </button>
        <button onClick={() => window.print()}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90"
          data-testid="print-btn">
          <Printer className="h-4 w-4" /> Print
        </button>
      </div>

      {/* Email bar */}
      {showEmail && (
        <div className="print:hidden bg-card border-b px-6 py-4">
          <div className="flex gap-3 items-end max-w-xl">
            <div className="flex-1">
              <label className="block text-xs font-medium text-muted-foreground mb-1">Email To *</label>
              <input type="email" value={emailAddr} onChange={e => setEmailAddr(e.target.value)}
                placeholder="customer@example.com" className="w-full px-3 py-2 border border-input rounded-md text-sm" />
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-muted-foreground mb-1">Message</label>
              <input type="text" value={emailMsg} onChange={e => setEmailMsg(e.target.value)}
                placeholder="Additional message..." className="w-full px-3 py-2 border border-input rounded-md text-sm" />
            </div>
            <button onClick={sendEmail} disabled={emailing}
              className="px-4 py-2 bg-primary text-white rounded-md text-sm disabled:opacity-50">{emailing ? 'Sending...' : 'Send'}</button>
            <button onClick={() => setShowEmail(false)} className="px-3 py-2 border rounded-md text-sm hover:bg-muted"><X className="h-4 w-4" /></button>
          </div>
        </div>
      )}

      {/* Printable Statement */}
      <div className="max-w-[850px] mx-auto bg-white p-8 my-6 print:my-0 print:p-6 print:shadow-none shadow-lg" style={{color: '#111'}}>
        {/* Header */}
        <div className="flex justify-between items-start border-b-2 border-gray-800 pb-4 mb-6">
          <div className="flex items-start gap-3">
            <img src="/logo.jpg" alt="Webb Supply" className="h-14 w-14 rounded-md object-cover" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight" style={{color: '#111'}}>Webb Supply</h1>
              <div className="text-sm mt-1" style={{color: '#444'}}>
                <div>227 County Road 125 East</div>
                <div>Norris City, IL 62869</div>
                <div>618-962-3211</div>
              </div>
            </div>
          </div>
          <div className="text-right">
            <h2 className="text-3xl font-bold tracking-tight" style={{color: '#1a2744'}}>ACCOUNT STATEMENT</h2>
            <div className="text-sm mt-2" style={{color: '#444'}}>
              <div><strong>Date:</strong> {new Date().toLocaleDateString()}</div>
              <div><strong>Service Rate:</strong> {serviceRate}% / month (after 30 days)</div>
            </div>
          </div>
        </div>

        {/* Customer address block — positioned for #10 envelope window */}
        <div className="mb-6 text-sm" style={{marginTop: '0.15in', minHeight: '1in'}}>
          <div className="font-semibold text-xs uppercase tracking-wider mb-1" style={{color: '#666'}}>Statement For</div>
          <div className="font-semibold text-base" style={{color: '#111'}}>{decoded}</div>
          {statement.customer_info?.address && <div>{statement.customer_info.address}</div>}
          {(statement.customer_info?.city || statement.customer_info?.state || statement.customer_info?.zip_code) && (
            <div>{[statement.customer_info.city, statement.customer_info.state, statement.customer_info.zip_code].filter(Boolean).join(', ')}</div>
          )}
          {statement.customer_info?.phone && <div style={{color: '#444'}}>{statement.customer_info.phone}</div>}
          {statement.customer_info?.email && <div style={{color: '#444'}}>{statement.customer_info.email}</div>}
        </div>

        {/* Unpaid Invoices */}
        {pendingInvoices.length > 0 && (
          <>
            <h3 className="text-sm font-bold uppercase text-gray-600 mb-2">Unpaid Invoices ({pendingInvoices.length})</h3>
            <table className="w-full text-sm mb-6" style={{borderCollapse: 'collapse'}}>
              <thead>
                <tr style={{backgroundColor: '#1a2744', color: 'white'}}>
                  <th className="text-left px-3 py-2 font-medium">Invoice #</th>
                  <th className="text-left px-3 py-2 font-medium">Date</th>
                  <th className="text-right px-3 py-2 font-medium">Days</th>
                  <th className="text-right px-3 py-2 font-medium">Invoice Total</th>
                  <th className="text-right px-3 py-2 font-medium">Service Chg</th>
                  <th className="text-right px-3 py-2 font-medium">Amount Due</th>
                  <th className="text-center px-3 py-2 font-medium print:hidden">Action</th>
                </tr>
              </thead>
              <tbody>
                {pendingInvoices.map((inv, i) => (
                  <tr key={inv.id} style={{borderBottom: '1px solid #e0e0e0', backgroundColor: i % 2 === 0 ? '#fff' : '#f8f8f8'}}>
                    <td className="px-3 py-2 font-medium">
                      {inv.order_number}
                      {inv.payment_status === 'partial' && (
                        <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-200 text-amber-900" title={`Paid $${(inv.amount_paid || 0).toFixed(2)} of $${(inv.total || 0).toFixed(2)}`}>PARTIAL</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-500">{fmtDate(inv.due_date || inv.order_date)}</td>
                    <td className="px-3 py-2 text-right">
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${inv.days_overdue > 60 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                        {inv.days_overdue}d
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {fmtCurrency(inv.total)}
                      {inv.payment_status === 'partial' && Number(inv.amount_paid) > 0 && (
                        <div className="text-[11px] text-green-700">paid: {fmtCurrency(inv.amount_paid)}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-amber-600">{fmtCurrency(inv.service_charge)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmtCurrency(inv.amount_due)}</td>
                    <td className="px-3 py-2 text-center print:hidden">
                      <button onClick={() => markPaid(inv.id)}
                        className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 font-medium"
                        data-testid={`mark-paid-${inv.id}`}>
                        Mark Paid
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Paid Invoices */}
        {paidInvoices.length > 0 && (
          <>
            <h3 className="text-sm font-bold uppercase text-green-700 mb-2">Paid Invoices ({paidInvoices.length})</h3>
            <table className="w-full text-sm mb-6 opacity-70" style={{borderCollapse: 'collapse'}}>
              <thead>
                <tr style={{backgroundColor: '#166534', color: 'white'}}>
                  <th className="text-left px-3 py-2 font-medium">Invoice #</th>
                  <th className="text-left px-3 py-2 font-medium">Date</th>
                  <th className="text-right px-3 py-2 font-medium">Invoice Total</th>
                  <th className="text-center px-3 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {paidInvoices.map((inv, i) => (
                  <tr key={inv.id} style={{borderBottom: '1px solid #e0e0e0', backgroundColor: i % 2 === 0 ? '#f0fdf4' : '#fff'}}>
                    <td className="px-3 py-2">{inv.order_number}</td>
                    <td className="px-3 py-2 text-gray-500">{fmtDate(inv.due_date || inv.order_date)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtCurrency(inv.total)}</td>
                    <td className="px-3 py-2 text-center"><span className="px-2 py-0.5 rounded text-xs font-medium bg-green-200 text-green-800">Paid</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Return Credits */}
        {statement.returns && statement.returns.length > 0 && (
          <>
            <h3 className="text-sm font-bold uppercase text-red-700 mb-2">Return Credits ({statement.returns.length})</h3>
            <table className="w-full text-sm mb-6" style={{borderCollapse: 'collapse'}}>
              <thead>
                <tr style={{backgroundColor: '#8b0000', color: 'white'}}>
                  <th className="text-left px-3 py-2 font-medium">Credit #</th>
                  <th className="text-left px-3 py-2 font-medium">Date</th>
                  <th className="text-left px-3 py-2 font-medium">Orig. SO #</th>
                  <th className="text-right px-3 py-2 font-medium">Items</th>
                  <th className="text-right px-3 py-2 font-medium">Credit Amount</th>
                </tr>
              </thead>
              <tbody>
                {statement.returns.map((ret, i) => (
                  <tr key={ret.id} style={{borderBottom: '1px solid #e0e0e0', backgroundColor: i % 2 === 0 ? '#fff' : '#fff5f5'}}>
                    <td className="px-3 py-2 font-medium text-red-700">{ret.return_number}</td>
                    <td className="px-3 py-2 text-gray-500">{fmtDate(ret.return_date)}</td>
                    <td className="px-3 py-2">{ret.sales_order_number}</td>
                    <td className="px-3 py-2 text-right">{(ret.line_items || []).length}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-red-700">-{fmtCurrency(ret.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Totals */}
        <div className="flex justify-end">
          <div className="w-80 border-t-2 border-gray-800 pt-3">
            <div className="flex justify-between py-1 text-sm">
              <span>Unpaid Principal:</span>
              <span className="tabular-nums">{fmtCurrency(statement.total_principal)}</span>
            </div>
            <div className="flex justify-between py-1 text-sm">
              <span>Service Charges:</span>
              <span className="tabular-nums text-amber-600">{fmtCurrency(statement.total_service_charges)}</span>
            </div>
            {(statement.total_return_credits || 0) > 0 && (
              <div className="flex justify-between py-1 text-sm text-red-700">
                <span>Return Credits:</span>
                <span className="tabular-nums">-{fmtCurrency(statement.total_return_credits)}</span>
              </div>
            )}
            <div className="flex justify-between py-2 text-xl font-bold border-t-2 border-gray-800 mt-2">
              <span>NET AMOUNT DUE:</span>
              <span className="tabular-nums text-red-700">{fmtCurrency(statement.grand_total)}</span>
            </div>
            {statement.total_paid > 0 && (
              <div className="flex justify-between py-1 text-sm text-green-600">
                <span>Already Paid:</span>
                <span className="tabular-nums">{fmtCurrency(statement.total_paid)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-10 text-center text-xs text-gray-400 border-t pt-4">
          <p>Please remit payment at your earliest convenience.</p>
          <p className="mt-1">Webb Supply - 227 County Road 125 East, Norris City, IL 62869 - 618-962-3211</p>
        </div>
      </div>
    </div>
  );
}
